import { ref, computed } from 'vue';
import { performanceManager, getCurrentConfig, shouldEnableGlass } from 'dashboard/utils/glassPerformance';

/**
 * Composable para gestionar efectos de vidrio líquido en Chatventas
 * Proporciona presets temáticos y configuraciones optimizadas
 */
export function useGlassEffect() {
  // Estado reactivo para configuraciones globales
  const isGlassEnabled = ref(shouldEnableGlass());
  const performanceMode = ref('auto'); // 'auto', 'high', 'low'
  const defaultEffect = ref('liquidGlass');

  // Presets temáticos para Chatventas
  const presets = {
    // Preset principal para tarjetas y paneles importantes
    chatventasCard: {
      displacementScale: 56,
      blurAmount: 8, // Cambio a 8px para mejor efecto de vidrio
      saturation: 125,
      aberrationIntensity: 1.8,
      elasticity: 0.25,
      cornerRadius: 12,
      padding: '20px 24px',
      backgroundOpacity: 0.05, // Muy transparente para el efecto flotante
      shadowIntensity: 0.2, // Sombra más visible para el efecto flotante
      borderOpacity: 0.15, // Borde sutil
      effect: 'liquidGlass',
      mode: 'standard',
      overLight: false,
    },

    // Preset para botones principales
    chatventasButton: {
      displacementScale: 48,
      blurAmount: 0.06,
      saturation: 130,
      aberrationIntensity: 2,
      elasticity: 0.35,
      cornerRadius: 8,
      padding: '12px 16px',
      effect: 'liquidGlass',
      mode: 'standard',
      overLight: false,
    },

    // Preset para modales y elementos emergentes
    chatventasModal: {
      displacementScale: 64,
      blurAmount: 0.1,
      saturation: 140,
      aberrationIntensity: 2.2,
      elasticity: 0.2,
      cornerRadius: 16,
      padding: '24px 32px',
      effect: 'unevenGlass',
      mode: 'prominent',
      overLight: false,
    },

    // Preset para navegación y sidebars
    chatventasNav: {
      displacementScale: 40,
      blurAmount: 0.04,
      saturation: 115,
      aberrationIntensity: 1.5,
      elasticity: 0.15,
      cornerRadius: 8,
      padding: '16px 20px',
      effect: 'transparentIce',
      mode: 'standard',
      overLight: false,
    },

    // Preset sutil para elementos secundarios
    chatventasSubtle: {
      displacementScale: 32,
      blurAmount: 0.03,
      saturation: 110,
      aberrationIntensity: 1,
      elasticity: 0.1,
      cornerRadius: 6,
      padding: '12px 16px',
      effect: 'flowingLiquid',
      mode: 'standard',
      overLight: false,
    },

    // Preset para burbujas de chat específicamente (basado en plantilla)
    chatventasBubble: {
      displacementScale: 50,
      blurAmount: 6, // Cambiado a px directamente
      saturation: 120,
      aberrationIntensity: 2,
      elasticity: 0.35,
      cornerRadius: 100, // 100px como en la plantilla
      padding: '8px 12px',
      backgroundOpacity: 0.08, // Reducido para menos opacidad
      shadowIntensity: 0.15, // Reducido para menos sombra
      borderOpacity: 0.2,
      effect: 'liquidGlass',
      mode: 'standard',
      overLight: false,
    },

    // Preset específico para el input de chat - máxima transparencia
    chatventasInput: {
      displacementScale: 40,
      blurAmount: 12, // Blur muy fuerte para ver contenido detrás claramente
      saturation: 105,
      aberrationIntensity: 1.2,
      elasticity: 0.15,
      cornerRadius: 12,
      padding: '16px 20px',
      backgroundOpacity: 0.01, // Prácticamente invisible
      shadowIntensity: 0.08, // Sombra muy ligera
      borderOpacity: 0.05, // Borde prácticamente invisible
      effect: 'liquidGlass',
      mode: 'standard',
      overLight: false,
    },

    // Preset para elementos de alta prioridad
    chatventasPremium: {
      displacementScale: 72,
      blurAmount: 0.12,
      saturation: 150,
      aberrationIntensity: 2.5,
      elasticity: 0.4,
      cornerRadius: 20,
      padding: '28px 36px',
      effect: 'mosaicGlass',
      mode: 'shader',
      overLight: false,
    },
  };

  // Configuraciones de rendimiento
  const performanceConfigs = {
    high: {
      // Configuración completa para dispositivos potentes
      mode: 'shader',
      maxDisplacementScale: 80,
      enableAllEffects: true,
    },
    auto: {
      // Configuración equilibrada (default)
      mode: 'standard',
      maxDisplacementScale: 64,
      enableAllEffects: true,
    },
    low: {
      // Configuración reducida para dispositivos menos potentes
      mode: 'standard',
      maxDisplacementScale: 40,
      enableAllEffects: false,
      fallbackEffect: 'flowingLiquid',
    },
  };

  // Detectar capacidades del dispositivo usando el gestor de rendimiento
  const deviceCapabilities = computed(() => {
    return performanceManager.detector.capabilities;
  });

  // Aplicar configuración de rendimiento automática
  const getOptimizedConfig = (presetName) => {
    const preset = presets[presetName] || presets.chatventasCard;
    const currentConfig = getCurrentConfig();

    // Si los efectos están deshabilitados por rendimiento, devolver null
    if (!currentConfig || !shouldEnableGlass()) {
      return null;
    }

    const optimizedConfig = { ...preset };

    // Aplicar configuraciones de rendimiento
    if (currentConfig.settings) {
      Object.assign(optimizedConfig, currentConfig.settings);
    }

    // Aplicar factor de calidad
    if (currentConfig.quality < 1.0) {
      optimizedConfig.displacementScale = Math.floor(optimizedConfig.displacementScale * currentConfig.quality);
      optimizedConfig.aberrationIntensity *= currentConfig.quality;
      optimizedConfig.blurAmount *= currentConfig.quality;
    }

    return optimizedConfig;
  };

  // Obtener configuración de preset específico
  const getPreset = (presetName) => {
    return presets[presetName] || null;
  };

  // Crear configuración personalizada combinando preset base con overrides
  const createCustomConfig = (config) => {
    return {
      ...presets.chatventasCard, // Base por defecto
      ...config
    };
  };

  // Configurar preset por defecto
  const setDefaultPreset = (presetName) => {
    if (presets[presetName]) {
      defaultEffect.value = presetName;
    } else {
      console.warn(`Preset '${presetName}' no encontrado, usando chatventasCard`);
      defaultEffect.value = 'chatventasCard';
    }
  };

  // Alternar efectos de vidrio globalmente
  const toggleGlassEffects = () => {
    isGlassEnabled.value = !isGlassEnabled.value;

    // Actualizar configuración global de rendimiento
    performanceManager.updateSettings({
      enableGlass: isGlassEnabled.value
    });
  };

  // Cambiar modo de rendimiento
  const setPerformanceMode = (mode) => {
    if (performanceConfigs[mode]) {
      performanceMode.value = mode;

      // Aplicar configuración al gestor de rendimiento
      performanceManager.updateSettings(performanceConfigs[mode]);
    }
  };

  // Monitor de rendimiento en tiempo real
  const performanceMonitor = computed(() => {
    const stats = performanceManager.getStats();
    const currentPerf = performanceManager.getCurrentPerformance();

    return {
      fps: stats.fps,
      memory: stats.memory,
      quality: currentPerf.quality,
      recommendations: currentPerf.recommendations
    };
  });

  // Validar configuración de preset
  const validatePreset = (config) => {
    const required = ['displacementScale', 'blurAmount', 'saturation'];
    return required.every(prop => typeof config[prop] === 'number' && config[prop] >= 0);
  };

  // Crear preset personalizado
  const createCustomPreset = (name, config) => {
    if (!validatePreset(config)) {
      throw new Error(`Configuración de preset inválida para '${name}'`);
    }

    presets[name] = {
      ...presets.chatventasCard, // Base por defecto
      ...config
    };
  };

  // Estadísticas y debugging
  const getDebugInfo = () => {
    return {
      enabled: isGlassEnabled.value,
      mode: performanceMode.value,
      availablePresets: Object.keys(presets),
      currentConfig: getCurrentConfig(),
      deviceCapabilities: deviceCapabilities.value,
      performance: performanceMonitor.value
    };
  };

  return {
    // Estado reactivo
    isGlassEnabled,
    performanceMode,
    defaultEffect,

    // Configuraciones
    presets,
    getOptimizedConfig,
    getPreset,
    createCustomConfig,

    // Métodos de control
    setDefaultPreset,
    toggleGlassEffects,
    setPerformanceMode,

    // Gestión de presets
    createCustomPreset,
    validatePreset,

    // Monitoring
    performanceMonitor,
    deviceCapabilities,

    // Debugging
    getDebugInfo
  };
}

// Hook específico para obtener configuración de preset
export function useGlassPreset(presetName) {
  const { getOptimizedConfig, presets } = useGlassEffect();

  const config = computed(() => {
    return getOptimizedConfig(presetName);
  });

  const isAvailable = computed(() => {
    return config.value !== null;
  });

  return {
    config,
    isAvailable,
    preset: computed(() => presets[presetName] || null)
  };
}
